/// <reference types="@arcgis/map-components/types/react" />
import { config } from '../../config';
import { useCityBoundary } from '../features/queries/getCityBoundary';
import { createCityMaskLayer, minScaleFor } from '../lib/cityBoundary';
import {
  useGeoLocation,
  type GeoLocation,
} from '../providers/LocationProvider';
import { __ } from '../utils';
import MapLoader from './MapLoader';
import MissingLocationIndicator from './MissingLocationIndicator';
import { GlobalStyles, Box } from '@presov.sk/presov-ui-react';
import React from 'react';

const arcgisComponents = Promise.all([
  import('@arcgis/map-components/dist/components/arcgis-map'),
  import('@arcgis/map-components/dist/components/arcgis-zoom'),
  import('@arcgis/map-components/components/arcgis-locate'),
  import('@arcgis/map-components/components/arcgis-search'),
]);

// ArcGIS kreslí okolo plochy mapy modrý rám na každý `:focus` - teda aj po
// obyčajnom kliknutí myšou, kde len prekáža. Necháme ho len pre
// `:focus-visible`, čiže pre klávesnicu, kde je to jediná stopa, že je mapa
// zaostrená a reaguje na šípky. Plocha žije v shadow DOM `arcgis-map`, kam
// globálne CSS nedosiahne, takže hárok vkladáme priamo do jeho shadow rootu.
const focusOutlineSheet = new CSSStyleSheet();
focusOutlineSheet.replaceSync(
  '.esri-view-surface:focus:not(:focus-visible)::after { outline: none; }',
);

export type MapViewport = {
  extent: __esri.Extent;
  center: __esri.Point;
};

export type ArcgisMapProps = {
  ref?: React.Ref<HTMLArcgisMapElement>;
  onReady?: (map: HTMLArcgisMapElement) => void;
  onMove?: (viewport: MapViewport) => void;
  onClick?: (event: __esri.ViewClickEvent) => void;
  onAction?: () => void;
  onPopupVisible?: (isVisible: boolean) => void;
  onZoom?: (zoom: number) => void;
  onSearchResult?: (result: __esri.SearchSelectResultEvent) => void;
  search?: boolean;
  /**
   * Kam patrí vyhľadávanie adresy. `top-right` je predvolené a platí všade,
   * kde mapa vypĺňa plochu sama. Na hlavnej stránke leží vpravo plávajúci
   * panel, takže by hľadanie skončilo **pod ním** a nedalo by sa naň kliknúť -
   * tam sa posúva doľava, nad ovládanie priblíženia.
   */
  searchPosition?: 'top-left' | 'top-right';
  /**
   * Vlastné hľadanie namiesto widgetu ArcGISu. Keď príde, `<arcgis-search>`
   * sa nevykreslí a `searchPosition` je bezpredmetné - uzol ide do slotu
   * `top-left`, teda nad ovládanie priblíženia. Používa ho hlavná stránka na
   * **mobile**, kde je hľadanie na plnú šírku a ovládanie sa mu má zoradiť
   * pod neho; na desktope si ho kreslí ako prekryv v strede horného okraja, bez
   * slotu. Ostatné mapy v appke zostávajú na widgete.
   */
  searchSlot?: React.ReactNode;
  locationIndicator?: boolean;
  hideUserLocation?: boolean;
  disableAction?: boolean;
  initialLocation?: GeoLocation;
  initialZoom?: number;
  disablePopup?: boolean;
  /** Allow single finger pan on touch devices (default: false, requires two fingers) */
  allowSingleFingerPan?: boolean;
  /**
   * Zašedená a vyšrafovaná maska mimo územia mesta plus obmedzenie najmenšieho
   * priblíženia na to, v ktorom je Prešov akurát celý vidieť. Predvolene
   * zapnuté - appka je o meste, takže je to východiskový stav každej mapy,
   * nie doplnok. Posun mimo mesta sa **neblokuje** zámerne: gumené zastavenie
   * na okraji je na dotyku nepríjemné a von aj tak nie je za čím ísť.
   */
  cityBoundary?: boolean;
  /**
   * Mapa, ktorá si **polohu zariadenia nevyžiada vôbec** - ani pri načítaní,
   * ani tlačidlom. Nemá lokalizačné tlačidlo, značku „vaša poloha" ani
   * indikátor chýbajúcej polohy. Je to mapa o mieste niečoho iného (filter
   * lokality v administrácii je o mieste podnetu, nie úradníka) a pýtať si
   * tam povolenie by bolo zbytočné, v prehliadači so zakázanou polohou aj
   * otravné.
   *
   * Bez tohto propu si mapa polohu pýta pri načítaní sama, vždy **ticho**
   * (`silent`) - toast hlási len akcia používateľa.
   */
  withoutDeviceLocation?: boolean;
};

export const ArcgisMap: React.FC<ArcgisMapProps> = ({
  ref,
  onReady,
  onMove,
  onClick,
  onAction,
  onPopupVisible,
  onZoom,
  onSearchResult,
  search,
  searchPosition = 'top-right',
  searchSlot,
  locationIndicator,
  hideUserLocation,
  initialLocation,
  disableAction,
  initialZoom,
  disablePopup,
  allowSingleFingerPan = false,
  cityBoundary = true,
  withoutDeviceLocation = false,
}) => {
  const defaultLocation = config.DEFAULT_POSITION;

  const { getCurrentPosition, locationStatus } = useGeoLocation();
  const { ring: cityRing, isInside } = useCityBoundary();
  React.use(arcgisComponents);

  const [currentLocation, setCurrentLocation] =
    React.useState<GeoLocation | null>(initialLocation ?? defaultLocation);
  const [locationGraphic, setLocationGraphic] =
    React.useState<__esri.Graphic | null>(null);
  const [mapLoading, setMapLoading] = React.useState<boolean>(true);
  const [showTwoFingerOverlay, setShowTwoFingerOverlay] =
    React.useState<boolean>(false);

  const [isViewReady, setIsViewReady] = React.useState<boolean>(false);
  // Surová poloha zariadenia. `currentLocation` na to nestačí - to je stred
  // mapy a keď poloha nie je k dispozícii, drží `DEFAULT_POSITION`, takže sa
  // z neho nedá rozoznať „som v Prešove" od „polohu nemám".
  const [devicePosition, setDevicePosition] =
    React.useState<GeoLocation | null>(null);

  const mapElementRef = React.useRef<HTMLArcgisMapElement | null>(null);
  const recenterBtnRef = React.useRef<HTMLDivElement | null>(null);
  const isRequestingLocationRef = React.useRef<boolean>(false);
  const overlayTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const hasFramedOutsideRef = React.useRef<boolean>(false);

  // Kým je poloha zakázaná, nemá zmysel ukazovať lokalizačné tlačidlo ani
  // značku polohy. Provider tento stav prepína aj počas života stránky
  // (Permissions API), takže sa oboje vráti bez reloadu.
  // **'UNAVAILABLE' tlačidlo neskrýva.** To je dočasné zlyhanie systému
  // (Wi-Fi, VPN), nie rozhodnutie používateľa, a bez tlačidla by sa pokus dal
  // zopakovať len refreshom stránky - teda práve tým, čo znova vyvolá dialóg
  // o povolení. Že poloha chýba, hovorí vedľa neho indikátor.
  const isLocationPermitted = locationStatus !== 'DENY';
  const showsUserLocation =
    !hideUserLocation && !withoutDeviceLocation && isLocationPermitted;

  // Mala mapa polohu **už pri prvom vykreslení**? To je poloha z adresy
  // (plus kód, `?lat=&lon=`) a tá má prednosť pred zarámovaním mesta pre
  // zariadenie mimo Prešova. Neskoršiu `initialLocation` sa rátať nesmie:
  // hlavná stránka do nej posiela aj stred mapy, ktorý sa naplní hneď po
  // načítaní - a zarámovanie by sa potom nespustilo nikdy.
  const [hasInitialLocation] = React.useState(() => !!initialLocation);

  React.useEffect(() => {
    // O polohu žiadame v stave 'CHECK' - po zmene povolenia v prehliadači nás
    // doň provider vráti, takže sa pokus zopakuje bez reloadu stránky. Ref drží
    // len rozbehnutý request, aby sme nepýtali dvakrát naraz. Keď rodič poslal
    // initialLocation, polohu si zistíme kvôli značke, ale mapou nehýbeme -
    // o to sa starajú podmienky !initialLocation nižšie.
    if (
      withoutDeviceLocation ||
      locationStatus !== 'CHECK' ||
      isRequestingLocationRef.current
    )
      return;
    isRequestingLocationRef.current = true;

    getCurrentPosition({ silent: true }).then((location) => {
      isRequestingLocationRef.current = false;

      if (location) {
        setDevicePosition(location);

        if (!initialLocation) {
          setCurrentLocation(location);
        }

        import('@arcgis/core/Graphic').then(({ default: Graphic }) => {
          const graphic = new Graphic({
            geometry: {
              type: 'point',
              longitude: location[0],
              latitude: location[1],
            },
            symbol: {
              type: 'picture-marker',
              url: '/MyLocationIcon.svg',
              width: '32px',
              height: '32px',
            },
            popupTemplate: {
              title: __('Vaša poloha'),
              content: __('Toto je vaša aktuálna poloha.'),
            },
          });

          setLocationGraphic(graphic);
        });
      } else {
        if (!initialLocation) {
          setCurrentLocation(defaultLocation);
        }
      }
    });
  }, [
    getCurrentPosition,
    initialLocation,
    defaultLocation,
    locationStatus,
    withoutDeviceLocation,
  ]);

  React.useEffect(() => {
    import('@arcgis/core/intl').then(({ setLocale }) => setLocale('sk'));
  }, []);

  // Update button disabled state
  React.useEffect(() => {
    if (recenterBtnRef.current) {
      if (disableAction) {
        recenterBtnRef.current.style.pointerEvents = 'none';
        recenterBtnRef.current.style.opacity = '0.5';
        recenterBtnRef.current.setAttribute('aria-disabled', 'true');
      } else {
        recenterBtnRef.current.style.pointerEvents = 'auto';
        recenterBtnRef.current.style.opacity = '1';
        recenterBtnRef.current.removeAttribute('aria-disabled');
      }
    }
  }, [disableAction]);

  const handleReady = React.useCallback(
    ({ target }: { target: HTMLArcgisMapElement }) => {
      // Enable browser scroll with one finger and map navigation with two fingers on touch devices
      // Unless allowSingleFingerPan is true, then allow single finger pan
      if (target.view?.navigation) {
        target.view.navigation.browserTouchPanEnabled = allowSingleFingerPan;
      }

      // Add touch event listeners to detect single finger swipe (only if two fingers required)
      const mapContainer = target.view?.container;
      if (mapContainer && !allowSingleFingerPan) {
        let touchStartTime = 0;
        let touchCount = 0;

        const handleTouchStart = (e: TouchEvent) => {
          touchCount = e.touches.length;
          touchStartTime = Date.now();
        };

        const handleTouchMove = (e: TouchEvent) => {
          // Show overlay only if user is trying to pan with one finger
          if (touchCount === 1 && e.touches.length === 1) {
            const touchDuration = Date.now() - touchStartTime;
            // Only show if touch has been held for a bit (not just a tap)
            if (touchDuration > 50) {
              setShowTwoFingerOverlay(true);

              // Clear existing timeout
              if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
              }

              // Hide overlay after 2 seconds
              overlayTimeoutRef.current = setTimeout(() => {
                setShowTwoFingerOverlay(false);
              }, 2000);
            }
          }
        };

        const handleTouchEnd = () => {
          touchCount = 0;
        };

        mapContainer.addEventListener('touchstart', handleTouchStart, {
          passive: true,
        });
        mapContainer.addEventListener('touchmove', handleTouchMove, {
          passive: true,
        });
        mapContainer.addEventListener('touchend', handleTouchEnd, {
          passive: true,
        });
      }

      const shadow = target.shadowRoot;
      if (shadow && !shadow.adoptedStyleSheets.includes(focusOutlineSheet)) {
        shadow.adoptedStyleSheets = [
          ...shadow.adoptedStyleSheets,
          focusOutlineSheet,
        ];
      }

      mapElementRef.current = target;
      setIsViewReady(true);

      onReady?.(target);

      if (initialZoom) {
        target.zoom = initialZoom;
      }

      if (onAction && !withoutDeviceLocation && !recenterBtnRef.current) {
        const btn = document.createElement('div');
        btn.className = 'esri-widget esri-widget--button esri-interactive';
        btn.innerHTML = '<span class="esri-icon-locate"></span>';
        btn.title = __('action button');

        btn.addEventListener('click', () => {
          onAction();
        });

        recenterBtnRef.current = btn;

        // add (or move) the button into the UI
        target.view.ui.add(recenterBtnRef.current, 'top-left');
      }

      import('@arcgis/core/core/reactiveUtils').then(({ watch }) => {
        watch(
          () => target.view.popup.visible,
          (isVisible) => {
            onPopupVisible?.(isVisible);
          },
        );

        watch(
          () => target.view.zoom,
          (zoom) => {
            if (zoom > 0) {
              onZoom?.(zoom);
            }
          },
        );

        // Watch for view updating state (true while tiles/layers are loading)
        watch(
          () => target.view.updating,
          (updating) => {
            if (!updating) {
              setMapLoading(false);
            }
          },
          { initial: true },
        );
      });
    },
    [
      onReady,
      initialZoom,
      onAction,
      withoutDeviceLocation,
      onPopupVisible,
      onZoom,
      setShowTwoFingerOverlay,
      allowSingleFingerPan,
    ],
  );

  // Vrstvu so značkou vašej polohy spravujeme samostatne, nie v handleReady:
  // ten sa spustí raz pri pripravení mapy, takže značka, ktorá pribudne až po
  // dodatočnom povolení polohy, by sa na mapu nikdy nedostala. Cleanup vrstvu
  // odoberie, takže sa pri odobraní povolenia zo mapy stratí aj značka.
  React.useEffect(() => {
    const map = mapElementRef.current?.map;

    if (!isViewReady || !map || !showsUserLocation || !locationGraphic) {
      return;
    }

    let isCancelled = false;

    import('@arcgis/core/layers/GraphicsLayer').then(
      ({ default: GraphicsLayer }) => {
        if (isCancelled) return;

        const graphicsLayer = new GraphicsLayer({
          id: 'location-layer',
          title: 'User Location',
        });
        graphicsLayer.add(locationGraphic);
        map.add(graphicsLayer);
      },
    );

    return () => {
      isCancelled = true;
      const layer = map.findLayerById('location-layer');
      if (layer) {
        map.remove(layer);
      }
    };
  }, [isViewReady, showsUserLocation, locationGraphic]);

  // Maska územia mesta a najmenšie priblíženie.
  //
  // Nie je to v `handleReady`: hranica prichádza z API, teda až po tom, čo je
  // mapa pripravená. Kým nedorazí (alebo keď dotaz padne), mapa sa správa
  // presne ako predtým - bez masky a bez obmedzenia. Je to tak schválne:
  // tvrdú poistku územia drží `createReport` na serveri a appka sa nemá
  // zablokovať preto, že nedobehol jeden dotaz na dekoráciu.
  React.useEffect(() => {
    const element = mapElementRef.current;
    const map = element?.map;
    const view = element?.view;

    if (!isViewReady || !cityBoundary || !cityRing || !map || !view) {
      return;
    }

    let isCancelled = false;
    let sizeHandle: IHandle | undefined;

    Promise.all([
      createCityMaskLayer(cityRing),
      import('@arcgis/core/core/reactiveUtils'),
    ]).then(([{ layer, extent }, { watch }]) => {
      if (isCancelled) return;

      // Index 0: maska patrí pod značky feedu, nie nad ne.
      map.add(layer, 0);

      const applyConstraints = () => {
        const minScale = minScaleFor(extent, view.width, view.height);
        if (!minScale) return;

        view.constraints = {
          ...view.constraints,
          minScale,
          // Bez tohto by mierka sadla na najbližšiu úroveň podkladu a mesto by
          // buď nebolo celé vidieť, alebo by okolo neho ostal zbytočný lem.
          snapToZoom: false,
          // Ďalej než na obálku mesta sa mapa posunúť nedá - do Bratislavy sa
          // odísť nedalo za čím. Obmedzuje sa **stred** záberu, nie okraje,
          // takže pri kraji je stále vidieť okolie mesta; a je to obálka, nie
          // samotný polygón, lebo pri jeho zálivoch by sa mapa zasekávala na
          // miestach, kde to zvonku nemá vysvetlenie.
          geometry: extent,
        };
      };

      applyConstraints();
      sizeHandle = watch(() => [view.width, view.height], applyConstraints);

      // Keď je používateľ mimo mesta, mapa sa otvorí čo najviac oddialená
      // a vycentrovaná na Prešov. Zmysel je vidieť naraz mesto **aj** vlastnú
      // bodku - preto sa mapa na jeho polohu necentruje: `constraints.geometry`
      // by stred aj tak pritiahol k okraju mesta a mesto by skončilo bokom.
      // Takto je mesto v strede a bodka vyjde pri okraji záberu, pokiaľ je
      // dosť blízko (Ľubotice, Šariš, Haniska áno; Bratislava, samozrejme, nie).
      //
      // Robí sa to **raz**: je to počiatočné nastavenie pohľadu, nie správanie,
      // ktoré má používateľovi brať mapu z rúk pri každom prekreslení.
      // So zadanou počiatočnou polohou (odkaz na miesto, plus kód) sa
      // nerámuje vôbec - tá má prednosť pred tým, kde stojí zariadenie.
      if (
        !hasFramedOutsideRef.current &&
        !hasInitialLocation &&
        devicePosition &&
        !isInside(devicePosition[0], devicePosition[1])
      ) {
        const minScale = minScaleFor(extent, view.width, view.height);
        if (minScale) {
          hasFramedOutsideRef.current = true;
          view
            .goTo({ center: defaultLocation, scale: minScale })
            .catch(() => undefined);
        }
      }
    });

    return () => {
      isCancelled = true;
      sizeHandle?.remove();

      const layer = map.findLayerById('city-boundary-mask');
      if (layer) map.remove(layer);
    };
  }, [
    isViewReady,
    cityBoundary,
    cityRing,
    devicePosition,
    isInside,
    defaultLocation,
    hasInitialLocation,
  ]);

  const handleChange = React.useCallback(
    ({ target }: { target: HTMLArcgisMapElement }) => {
      const { center, extent } = target.view;
      onMove?.({ center, extent });
    },
    [onMove],
  );

  const handleClick = React.useCallback(
    ({ detail }: { detail: __esri.ViewClickEvent }) => {
      onClick?.(detail);
    },
    [onClick],
  );

  const handleSearchResult = React.useCallback(
    ({ detail }: { detail: __esri.SearchSelectResultEvent }) => {
      onSearchResult?.(detail);
    },
    [onSearchResult],
  );

  const searchWidget = (
    <arcgis-search
      position={searchPosition}
      onarcgisSelectResult={handleSearchResult}
      messageOverrides={{
        useCurrentLocation: __('Použiť aktuálnu polohu'),
        noResults: __('Žiadne výsledky'),
        noResultsFoundForValue: __(
          'Nenašli sa žiadne výsledky pre hodnotu: {value}',
        ),
      }}
      popupDisabled={disablePopup}
      resultGraphicDisabled={disablePopup}
    />
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: 1,
        height: 1,
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          '.esri-ui-inner-container': {
            inset: `${theme.typography.pxToRem(8)} !important`,
          },
        })}
      />

      {mapLoading && <MapLoader />}

      {showTwoFingerOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 1,
            transition:
              'opacity 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out',
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              from: {
                opacity: 0,
              },
              to: {
                opacity: 1,
              },
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: 2,
              borderRadius: 1,
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: 500,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              textAlignLast: 'center',
              transform: 'scale(1)',
              transition: 'transform 0.3s ease-in-out',
              animation: 'scaleIn 0.3s ease-in-out',
              '@keyframes scaleIn': {
                from: {
                  transform: 'scale(0.9)',
                  opacity: 0,
                },
                to: {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }}
          >
            {__('Použite dva prsty na pohyb po mape')}
          </Box>
        </Box>
      )}

      <arcgis-map
        ref={ref}
        onarcgisViewReadyChange={handleReady}
        onarcgisViewChange={handleChange}
        onarcgisViewClick={handleClick}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        browserTouchPanEnabled={allowSingleFingerPan}
        basemap={config.CUSTOM_MAP_BASEMAP}
        center={currentLocation}
        zoom={13}
      >
        {/* Poradie v JSX je poradie v stohu slotu: hľadanie deklarované pred
            priblížením sedí nad ním, presne ako v dizajne. */}
        {searchSlot ? (
          // Kontejner slotu má `pointer-events: none`, aby sa cez prázdne
          // miesto dalo ťahať mapu; vlastnému hľadaniu ich treba vrátiť.
          <Box slot='top-left' sx={{ pointerEvents: 'auto' }}>
            {searchSlot}
          </Box>
        ) : search && searchPosition === 'top-left' ? (
          searchWidget
        ) : null}
        <arcgis-zoom position='top-left' />
        {showsUserLocation && (
          <arcgis-locate
            position='top-left'
            {...(locationGraphic && { graphic: locationGraphic })}
            popupDisabled
          />
        )}
        {locationIndicator && !withoutDeviceLocation && (
          // Slot 'top-left' je ten istý stoh ako zoom a lokalizácia, takže sa
          // indikátor zoradí pod ne - odsadenie 10 px si drží zoom sám.
          // Kontejner slotu má pointer-events: none, pre tooltip ich vraciame.
          <MissingLocationIndicator
            slot='top-left'
            size={32}
            sx={{
              pointerEvents: 'auto',
              // rovnaký tien, aký majú ArcGIS tlačidlá na svojom hoste
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            }}
          />
        )}
        {!searchSlot && search && searchPosition === 'top-right'
          ? searchWidget
          : null}
      </arcgis-map>
    </Box>
  );
};
